// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// import { requestSubscriptionLink } from 'services/paymentService';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { v4 as uuidv4 } from 'uuid';
import { analyticsService } from 'services/analytics/analyticsService';
import { useShippingAbandonTracking } from 'hooks/useShippingAbandonTracking';
import { getMelhorEnvioQuote } from 'services/shippingService';
import { getPackQuantity } from 'utils/subscriptionPlanFeatures';
import {
  formatCurrencyBRL,
  parsePlanPrice,
  roundCurrency,
  sumCurrency,
} from 'utils/currency';

import styles from './styles';
import { updateUserSubscription } from 'services/firebase/UpdateUserSubscription';
import { auth } from 'services/firebase/client';

interface ShippingOption {
  id: string;
  description: string;
  price: number;
  deliveryTime: string;
}

const commonOptions: ShippingOption[] = [
  {
    id: 'retira',
    description: 'Retira na Cervejaria (em Araraquara-SP)',
    price: 0.0,
    deliveryTime: 'De quarta à sábado - 18h às 23h',
  },
];

const SubscriptionShippingOptions = () => {
  const navigate = useNavigate();
  const {
    personalData,
    selectedPlan,
    shippingOption,
    setSelectedPlan,
    setShippingOption,
  } = useSubscription();
  const [selectedShipping, setSelectedShipping] = useState('');
  const [shippingPrice, setShippingPrice] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  const bottleCount = useMemo(
    () => getPackQuantity(selectedPlan?.label ?? selectedPlan?.pack ?? ''),
    [selectedPlan?.label, selectedPlan?.pack],
  );

  const insuranceValue = useMemo(() => {
    if (selectedPlan?.subtotal != null) {
      return roundCurrency(selectedPlan.subtotal);
    }

    return parsePlanPrice(selectedPlan?.price);
  }, [selectedPlan?.price, selectedPlan?.subtotal]);

  const [deliveryQuote, setDeliveryQuote] = useState<{
    price: number;
    deliveryTime: string;
    description: string;
  } | null>(null);

  const subscriptionPlanLabel = selectedPlan
    ? `${selectedPlan.label} - ${selectedPlan.type}`
    : undefined;

  const { markAdvanceClicked } = useShippingAbandonTracking(
    'shipping_page',
    deliveryQuote?.price ?? null,
    subscriptionPlanLabel,
  );

  useEffect(() => {
    setShippingOption(null);
    setSelectedShipping('');
    setShippingPrice(0);
  }, [setShippingOption]);

  useEffect(() => {
    const destinationPostalCode = personalData?.cep?.replace(/\D/g, '') ?? '';

    if (!selectedPlan || destinationPostalCode.length !== 8 || bottleCount <= 0) {
      setDeliveryQuote(null);
      return;
    }

    let cancelled = false;

    const fetchQuote = async () => {
      setIsLoadingQuote(true);
      setQuoteError('');

      analyticsService.trackShippingQuoteRequested({
        source: 'shipping_page',
        cepPrefix: destinationPostalCode,
        subscriptionPlan: subscriptionPlanLabel,
      });

      try {
        const quote = await getMelhorEnvioQuote({
          destinationPostalCode,
          destinationCity: personalData?.address?.city,
          destinationUf: personalData?.address?.uf,
          bottleCount,
          insuranceValue,
        });

        if (cancelled) {
          return;
        }

        setDeliveryQuote({
          price: quote.price,
          deliveryTime: quote.deliveryTimeLabel,
          description: 'Frete',
        });

        analyticsService.trackShippingQuoteSuccess({
          source: 'shipping_page',
          cepPrefix: destinationPostalCode,
          shippingPrice: quote.price,
          subscriptionPlan: subscriptionPlanLabel,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Erro ao cotar frete:', error);
        setDeliveryQuote(null);

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Não foi possível calcular o frete.';

        analyticsService.trackShippingQuoteError({
          source: 'shipping_page',
          cepPrefix: destinationPostalCode,
          subscriptionPlan: subscriptionPlanLabel,
          errorMessage,
        });

        setQuoteError(errorMessage);
      } finally {
        if (!cancelled) {
          setIsLoadingQuote(false);
        }
      }
    };

    void fetchQuote();

    return () => {
      cancelled = true;
    };
  }, [
    bottleCount,
    insuranceValue,
    personalData?.address?.city,
    personalData?.address?.uf,
    personalData?.cep,
    selectedPlan,
    subscriptionPlanLabel,
  ]);

  const applyShippingSelection = useCallback(
    (option: ShippingOption) => {
      const planSubtotal = roundCurrency(
        selectedPlan?.subtotal ?? parsePlanPrice(selectedPlan?.price),
      );
      const roundedPrice = roundCurrency(option.price);

      setSelectedShipping(option.id);
      setShippingPrice(roundedPrice);
      setShippingOption({
        id: option.id,
        price: roundedPrice,
        description: option.description,
      });

      if (selectedPlan) {
        setSelectedPlan({
          ...selectedPlan,
          frete: roundedPrice,
          total: sumCurrency(planSubtotal, roundedPrice),
          subtotal: planSubtotal,
        });
      }
    },
    [selectedPlan, setSelectedPlan, setShippingOption],
  );

  // Opções de frete dinâmicas baseadas na cotação da API
  const allShippingOptions = useMemo(() => {
    const deliveryOptions: ShippingOption[] = deliveryQuote
      ? [
          {
            id: 'economico',
            description: deliveryQuote.description,
            price: deliveryQuote.price,
            deliveryTime: deliveryQuote.deliveryTime,
          },
        ]
      : [];

    const isAraraquara =
      personalData?.address?.city === 'Araraquara' &&
      personalData?.address?.uf === 'SP';
    return isAraraquara
      ? [...deliveryOptions, ...commonOptions]
      : deliveryOptions;
  }, [
    deliveryQuote,
    personalData?.address?.city,
    personalData?.address?.uf,
  ]);

  // Seleciona automaticamente após cotação ou corrige preço desatualizado da prévia
  useEffect(() => {
    if (allShippingOptions.length === 0) {
      return;
    }

    const matched = allShippingOptions.find(o => o.id === selectedShipping);

    if (!matched) {
      applyShippingSelection(allShippingOptions[0]);
      return;
    }

    if (shippingPrice !== matched.price) {
      applyShippingSelection(matched);
    }
  }, [
    allShippingOptions,
    applyShippingSelection,
    selectedShipping,
    shippingPrice,
  ]);

  const handleShippingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const selectedOption = allShippingOptions.find(
        option => option.id === event.target.value,
      );

      if (selectedOption) {
        applyShippingSelection(selectedOption);
      }
    },
    [allShippingOptions, applyShippingSelection],
  );

  useEffect(() => {
    if (!selectedPlan || !personalData) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate('/');
    } else {
      analyticsService.trackPageView('/shipping', 'Opções de Frete');
      analyticsService.trackSubscriptionFlow(
        'shipping_options',
        selectedPlan.type,
      );
    }
  }, [navigate, personalData, selectedPlan]);

  const handleAdvance = useCallback(async () => {
    if (!selectedShipping) {
      // eslint-disable-next-line no-alert
      alert('Selecione uma opção de frete.');
      return;
    }

    if (!selectedPlan || !personalData?.email || !personalData?.fullName) {
      alert('Informações incompletas');
      return;
    }

    markAdvanceClicked();
    analyticsService.trackCta(
      'subscription_shipping_continue',
      'Ir para pagamento',
      {
        shipping_price: shippingPrice,
        subscription_plan: subscriptionPlanLabel ?? null,
      },
    );
    analyticsService.trackShippingAdvanceClick({
      source: 'shipping_page',
      isAuthenticated: Boolean(auth.currentUser),
      hadShippingQuote: deliveryQuote !== null,
      shippingPrice: shippingPrice,
      subscriptionPlan: subscriptionPlanLabel,
      redirectTo: '/payment',
    });

    const subscriptionID = uuidv4();
    try {
      setIsRedirecting(true);
      const updatedPlan = {
        ...selectedPlan,
        total: selectedPlan.total ?? 0,
        frete: shippingPrice,
        type: selectedPlan.type ?? '',
        price: selectedPlan.price ?? '',
        subscriptionID: subscriptionID,
      };

      setSelectedPlan(updatedPlan);
      const user = auth.currentUser;
      if (!user) throw new Error('Usuário não autenticado');

      await updateUserSubscription(user.uid, {
        id: subscriptionID,
        preapprovalId: '',
        planId: '',
        price: updatedPlan.total,
        quantity: 1,
        status: 'Aguardando pagamento',
        label: updatedPlan.label,
        type: updatedPlan.type,
        hasFreight: updatedPlan.frete > 0 ? true : false,
        freightPrice: updatedPlan.frete,
      });

      // // Chamada para função do backend
      // const { init_point } = await requestSubscriptionLink({
      //   email: personalData.email,
      //   userId: personalData.cpf,
      //   reason: `${updatedPlan.label} - ${updatedPlan.type}`,
      //   totalAmount: updatedPlan.total,
      //   backUrl: 'https://club42.com.br/sucesso',
      // });

      // // Redireciona para o Mercado Pago
      // window.location.href = init_point;
      navigate('/payment');
    } catch (err) {
      console.error('Erro ao iniciar assinatura:', err);
      alert('Erro ao criar assinatura. Tente novamente.');
      setIsRedirecting(false);
    }
  }, [
    deliveryQuote,
    markAdvanceClicked,
    navigate,
    personalData,
    selectedPlan,
    selectedShipping,
    setSelectedPlan,
    shippingPrice,
    subscriptionPlanLabel,
  ]);

  const subtotal = roundCurrency(
    selectedPlan?.subtotal ?? parsePlanPrice(selectedPlan?.price),
  );
  const total = sumCurrency(subtotal, shippingOption?.price ?? 0);

  return (
    <Box sx={styles.container}>
      <Grid container spacing={2}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={styles.subtitle}>
            ENDEREÇO SELECIONADO
          </Typography>
          <Card sx={styles.card}>
            <CardContent>
              <Typography variant="body1" sx={styles.addressText}>
                {personalData.address.street}, {personalData.address.number}
              </Typography>
              <Typography variant="body1" sx={styles.addressText}>
                {personalData.address.city} - {personalData.address.uf}
              </Typography>
              <Typography variant="body2" sx={styles.addressDetails}>
                {personalData.address.neighborhood} - {personalData.cep}
              </Typography>
              <Typography variant="body2" sx={styles.addressDetails}>
                {personalData.fullName}
              </Typography>
            </CardContent>
          </Card>
          {/* <Button
            variant="outlined"
            sx={styles.newAddressButton}
            endIcon={<ArrowForwardIosIcon />}
          >
            Adicionar um novo endereço
          </Button> */}

          <Box sx={styles.shippingSectionContainer}>
            <Typography variant="subtitle1" sx={styles.subtitle}>
              ESCOLHA A OPÇÃO DE FRETE
            </Typography>
            {isLoadingQuote && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Calculando frete...
                </Typography>
              </Box>
            )}
            {quoteError && !isLoadingQuote && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {quoteError}
              </Typography>
            )}
            <RadioGroup
              name="shipping"
              value={selectedShipping}
              onChange={handleShippingChange}
            >
              {allShippingOptions.map(option => (
                <Card key={option.id} sx={styles.card}>
                  <CardContent sx={styles.shippingOptionContainer}>
                    <FormControlLabel
                      value={option.id}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography
                            variant="body1"
                            sx={styles.shippingOptionTitle}
                          >
                            {option.description}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={styles.shippingOptionDetails}
                          >
                            {option.deliveryTime}
                          </Typography>
                        </Box>
                      }
                    />
                    <Typography variant="body1" sx={styles.shippingOptionPrice}>
                      {option.price === 0
                        ? 'GRÁTIS'
                        : `R$ ${formatCurrencyBRL(option.price)}`}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
            <Typography variant="caption" sx={styles.disclaimer}>
              * O prazo de entrega passa a valer a partir do despacho do pedido
            </Typography>
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={styles.subtitle}>
            RESUMO
          </Typography>
          <Card sx={styles.summaryCard}>
            <CardContent>
              <Typography variant="body1" sx={styles.productText}>
                {`${selectedPlan?.label} - ${selectedPlan?.type}`}
              </Typography>
              <Typography variant="body2" sx={styles.quantityText}>
                Quantidade: {selectedPlan?.quantity}
              </Typography>
              <Box sx={styles.summaryContainer}>
                <Typography variant="body2" sx={styles.summaryText}>
                  SUBTOTAL
                </Typography>
                <Typography variant="body2" sx={styles.summaryValue}>
                  R$ {formatCurrencyBRL(subtotal)}
                </Typography>
              </Box>
              <Box sx={styles.summaryContainer}>
                <Typography variant="body1" sx={styles.totalText}>
                  FRETE
                </Typography>
                <Typography variant="h6" sx={styles.totalValue}>
                  {shippingOption?.price === 0 || !shippingOption
                    ? 'GRÁTIS'
                    : `R$ ${formatCurrencyBRL(shippingOption.price)}`}
                </Typography>
              </Box>
              <Box sx={styles.summaryContainer}>
                <Typography variant="body1" sx={styles.totalText}>
                  TOTAL
                </Typography>
                <Typography variant="h6" sx={styles.totalValue}>
                  R$ {formatCurrencyBRL(total)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={styles.advanceButton}
                onClick={handleAdvance}
                disabled={
                  isRedirecting || isLoadingQuote || !selectedShipping || !!quoteError
                }
                startIcon={
                  isRedirecting ? (
                    <CircularProgress size={20} /> // exibe spinner no botão
                  ) : undefined
                }
              >
                {isRedirecting ? 'Carregando...' : 'AVANÇAR'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionShippingOptions;
